/**
 * Public recipient profile — `/u/:handle`
 * Shows the recipient's badge wall (visible awards only).
 */
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useFormatDate } from '../../hooks/useFormatDate';

const API_HOST = (() => {
  if (typeof window === 'undefined') return 'https://api.badges.ninja';
  return window.location.hostname.includes('staging')
    ? 'https://api-staging.badges.ninja'
    : 'https://api.badges.ninja';
})();

interface Profile {
  handle: string;
  displayName: string;
  awards: {
    count: number;
    items: Array<{
      id: string;
      image: string;
      issuedOn: string;
      badge: { name: string; issuer: { name: string } };
    }>;
  };
}

export function RecipientProfile() {
  const { handle } = useParams<{ handle: string }>();
  const { t } = useTranslation('common');
  const formatDate = useFormatDate();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!handle) return;
    fetch(`${API_HOST}/u/${encodeURIComponent(handle)}`)
      .then(r => r.json().then(b => ({ ok: r.ok, body: b })))
      .then(({ ok, body }) => { if (!ok) throw new Error(body.error || 'Profile not found'); setProfile(body); })
      .catch(e => setError(e.message));
  }, [handle]);

  if (error) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center text-center p-4">
      <div>
        <h1 className="text-xl font-bold text-gray-900 mb-2">{t('profilePage.notFound')}</h1>
        <p className="text-sm text-gray-500 mb-4">{error}</p>
        <Link to="/" className="text-sm text-primary-600 hover:underline">{t('award.verify.goHome')}</Link>
      </div>
    </div>
  );

  if (!profile) return (
    <div className="flex h-screen items-center justify-center bg-gray-50">
      <div className="h-8 w-8 rounded-full border-4 border-primary-200 border-t-primary-600 animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 py-12">
        <header className="text-center mb-10">
          <div className="mx-auto h-20 w-20 rounded-full bg-primary-50 border-4 border-primary-100 flex items-center justify-center text-3xl font-bold text-primary-700 mb-3">
            {profile.displayName.charAt(0).toUpperCase()}
          </div>
          <h1 className="text-2xl font-bold text-gray-900">{profile.displayName}</h1>
          <p className="text-sm text-gray-500 mt-1">@{profile.handle}</p>
          <p className="text-xs text-gray-400 mt-2">
            {t('profilePage.badgeCount', { count: profile.awards.count })}
          </p>
        </header>

        {profile.awards.items.length === 0 ? (
          <div className="rounded-2xl border border-gray-200 bg-white p-12 text-center text-sm text-gray-500">
            {t('profilePage.noPublicBadges')}
          </div>
        ) : (
          <div className="grid gap-4 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4">
            {profile.awards.items.map(a => {
              const guid = a.id.split('/').pop() || a.id;
              return (
                <Link key={a.id} to={`/awards/${guid}`} className="block rounded-2xl border border-gray-200 bg-white shadow-card hover:shadow-md transition-all overflow-hidden">
                  <div className="aspect-square bg-gray-50 flex items-center justify-center p-4">
                    <img src={a.image} alt={a.badge.name} className="max-h-full max-w-full object-contain" />
                  </div>
                  <div className="p-3 border-t border-gray-100">
                    <h3 className="text-sm font-semibold text-gray-900 truncate">{a.badge.name}</h3>
                    <p className="text-xs text-gray-500 truncate mt-0.5">{a.badge.issuer.name}</p>
                    <p className="text-[11px] text-gray-400 mt-0.5">{formatDate(a.issuedOn)}</p>
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        <p className="mt-8 text-[11px] text-gray-400 text-center">
          {t('profilePage.poweredBy')}{' '}<Link to="/" className="text-primary-600 hover:underline">badges.ninja</Link>
        </p>
      </div>
    </div>
  );
}
